import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardList, Search, LayoutGrid, List,
  Loader2, Eye, X, Clock, User, Phone, MapPin, Truck,
  DollarSign, Package, CalendarDays, RefreshCw, Printer,
  CheckCircle2, XCircle, ChefHat, Bike, Bell, Link2, Copy, ChevronRight, MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useThermalPrinter } from "@/hooks/useThermalPrinter";
import type { ReceiptData, DeliveryReceiptData } from "@/lib/escpos";
import type { Tables } from "@/integrations/supabase/types";

type Order = Tables<"orders">;
type OrderItem = Tables<"order_items">;
type OrderWithItems = Order & { items: OrderItem[] };

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof Clock; bg: string }> = {
  received: { label: "Recebido", color: "text-primary", icon: Clock, bg: "bg-primary/10 border-primary/30" },
  preparing: { label: "Em Preparo", color: "text-warning", icon: ChefHat, bg: "bg-warning/10 border-warning/30" },
  ready: { label: "Pronto", color: "text-success", icon: CheckCircle2, bg: "bg-success/10 border-success/30" },
  out_for_delivery: { label: "Saiu p/ Entrega", color: "text-primary", icon: Bike, bg: "bg-primary/10 border-primary/30" },
  completed: { label: "Finalizado", color: "text-muted-foreground", icon: CheckCircle2, bg: "bg-muted/50 border-muted" },
  cancelled: { label: "Cancelado", color: "text-destructive", icon: XCircle, bg: "bg-destructive/10 border-destructive/30" },
};

const ORDER_TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  dine_in: { label: "No Local", icon: "🍽️" },
  takeout: { label: "Retirada", icon: "🥡" },
  delivery: { label: "Delivery", icon: "🛵" },
  scheduled: { label: "Agendado", icon: "📅" },
};

const PAYMENT_LABELS: Record<string, string> = {
  pix: "PIX",
  credit_card: "Cartão de Crédito",
  debit_card: "Cartão de Débito",
  cash: "Dinheiro",
  mercadopago: "MercadoPago",
};

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

const openWhatsApp = (phone: string | null, customerName?: string | null) => {
  if (!phone) return;
  const cleaned = phone.replace(/\D/g, "");
  const number = cleaned.startsWith("55") ? cleaned : `55${cleaned}`;
  const msg = encodeURIComponent(`Olá${customerName ? ` ${customerName}` : ""}! Referente ao seu pedido no nosso restaurante.`);
  window.open(`https://wa.me/${number}?text=${msg}`, "_blank");
};
const formatDateTime = (d: string) => {
  const date = new Date(d);
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" }) +
    " " + date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

const timeAgo = (date: string) => {
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / 60000);
  if (diff < 1) return "agora";
  if (diff < 60) return `${diff}min`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h`;
  return `${Math.floor(diff / 1440)}d`;
};

const Pedidos = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const printer = useThermalPrinter();
  const [loading, setLoading] = useState(true);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [restaurantSlug, setRestaurantSlug] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrderWithItems[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<OrderWithItems | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Waiter requests from table tabs
  const [waiterRequests, setWaiterRequests] = useState<any[]>([]);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("today");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "highest" | "lowest">("newest");
  const [viewMode, setViewMode] = useState<"list" | "cards">(() => {
    return (localStorage.getItem("pedidos_view_mode") as "list" | "cards") || "cards";
  });

  const fetchOrders = useCallback(async (restId: string, retryCount = 0) => {
    const { data: ordersData } = await supabase
      .from("orders")
      .select("*")
      .eq("restaurant_id", restId)
      .order("created_at", { ascending: false });

    if (!ordersData) return;

    const orderIds = ordersData.map((o) => o.id);
    
    // Batch order IDs in chunks of 50 to avoid URL length limits
    let allItems: any[] = [];
    if (orderIds.length > 0) {
      const chunks: string[][] = [];
      for (let i = 0; i < orderIds.length; i += 50) {
        chunks.push(orderIds.slice(i, i + 50));
      }
      const results = await Promise.all(
        chunks.map((chunk) =>
          supabase.from("order_items").select("*").in("order_id", chunk)
        )
      );
      for (const r of results) {
        if (r.data) allItems.push(...r.data);
      }
    }

    const enriched: OrderWithItems[] = ordersData.map((o) => ({
      ...o,
      items: allItems.filter((i) => i.order_id === o.id),
    }));

    // Auto-retry if any recent order has subtotal but no items (race condition)
    if (retryCount < 2) {
      const missingItems = enriched.some(
        (o) => Number(o.subtotal) > 0 && o.items.length === 0 && 
               new Date(o.created_at).getTime() > Date.now() - 60000
      );
      if (missingItems) {
        setTimeout(() => fetchOrders(restId, retryCount + 1), 1500);
      }
    }

    setOrders(enriched);
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }
      
      // Check for admin impersonation
      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      let rest: any = null;
      
      if (impersonateId) {
        // Super admin impersonating a restaurant
        const { data } = await supabase
          .from("restaurants")
          .select("id, slug")
          .eq("id", impersonateId)
          .maybeSingle();
        rest = data;
      } else {
        // Regular restaurant owner
        const { data } = await supabase
          .from("restaurants")
          .select("id, slug")
          .eq("owner_id", user.id)
          .maybeSingle();
        rest = data;
      }
      
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurantSlug(rest.slug);
      setRestaurantId(rest.id);
      await fetchOrders(rest.id);
      setLoading(false);
    };
    init();
  }, [navigate, fetchOrders]);

  // Realtime — debounce to avoid race condition where items aren't inserted yet
  useEffect(() => {
    if (!restaurantId) return;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedFetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fetchOrders(restaurantId), 800);
    };
    const channel = supabase
      .channel("pedidos-orders")
      .on("postgres_changes", { event: "*", schema: "public", table: "orders", filter: `restaurant_id=eq.${restaurantId}` },
        debouncedFetch
      )
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "order_items" },
        debouncedFetch
      )
      .subscribe();
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [restaurantId, fetchOrders]);

  // Fetch and subscribe to waiter requests
  const fetchWaiterRequests = useCallback(async (restId: string) => {
    const { data } = await supabase
      .from("table_tabs" as any)
      .select("*")
      .eq("restaurant_id", restId)
      .eq("status", "open")
      .not("waiter_requested_at", "is", null)
      .order("waiter_requested_at", { ascending: true });
    setWaiterRequests((data as any[]) || []);
  }, []);

  useEffect(() => {
    if (!restaurantId) return;
    fetchWaiterRequests(restaurantId);
    const channel = supabase
      .channel("waiter-requests")
      .on("postgres_changes", { event: "*", schema: "public", table: "table_tabs", filter: `restaurant_id=eq.${restaurantId}` },
        () => fetchWaiterRequests(restaurantId)
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [restaurantId, fetchWaiterRequests]);

  const dismissWaiterRequest = async (tabId: string) => {
    await supabase
      .from("table_tabs" as any)
      .update({ waiter_requested_at: null } as any)
      .eq("id", tabId);
    if (restaurantId) fetchWaiterRequests(restaurantId);
  };

  const updateStatus = async (orderId: string, newStatus: string) => {
    const { error } = await supabase.from("orders").update({ status: newStatus as any }).eq("id", orderId);
    if (error) {
      toast({ title: "Erro ao atualizar", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Status atualizado!" });
      if (restaurantId) await fetchOrders(restaurantId);
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => prev ? { ...prev, status: newStatus as any } : null);
      }
      // WhatsApp notification (fire and forget)
      supabase.functions.invoke('whatsapp-notify-order', {
        body: { order_id: orderId, restaurant_id: restaurantId, status: newStatus },
      }).catch(() => {});
    }
  };

  // Filtered & sorted orders
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((o) =>
        o.id.toLowerCase().includes(q) ||
        o.customer_name?.toLowerCase().includes(q) ||
        o.customer_phone?.toLowerCase().includes(q) ||
        o.items.some((i) => i.product_name.toLowerCase().includes(q))
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter((o) => o.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== "all") {
      result = result.filter((o) => o.order_type === typeFilter);
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      const today = now.toISOString().slice(0, 10);
      if (dateFilter === "today") {
        result = result.filter((o) => o.created_at.slice(0, 10) === today);
      } else if (dateFilter === "week") {
        const weekAgo = new Date(now.getTime() - 7 * 86400000).toISOString();
        result = result.filter((o) => o.created_at >= weekAgo);
      } else if (dateFilter === "month") {
        const monthAgo = new Date(now.getTime() - 30 * 86400000).toISOString();
        result = result.filter((o) => o.created_at >= monthAgo);
      }
    }

    // Sort
    switch (sortBy) {
      case "newest": result.sort((a, b) => b.created_at.localeCompare(a.created_at)); break;
      case "oldest": result.sort((a, b) => a.created_at.localeCompare(b.created_at)); break;
      case "highest": result.sort((a, b) => Number(b.total) - Number(a.total)); break;
      case "lowest": result.sort((a, b) => Number(a.total) - Number(b.total)); break;
    }

    return result;
  }, [orders, search, statusFilter, typeFilter, dateFilter, sortBy]);

  // Stats
  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const todayOrders = orders.filter((o) => o.created_at.slice(0, 10) === today);
    const active = orders.filter((o) => ["received", "preparing", "ready", "out_for_delivery"].includes(o.status));
    const todayRevenue = todayOrders.filter(o => o.status !== "cancelled").reduce((s, o) => s + Number(o.total), 0);
    return {
      total: orders.length,
      active: active.length,
      today: todayOrders.length,
      todayRevenue,
    };
  }, [orders]);

  const openDetail = (order: OrderWithItems) => {
    setSelectedOrder(order);
    setDetailOpen(true);
  };

  const printOrder = async (order: OrderWithItems) => {
    // Enrich data for printing
    const { fetchRestaurantInfo, fetchCustomerOrderCount, fetchCustomerLoyaltyPoints, fetchWaiterNameForOrder, fetchIfoodReference, enrichOrderItemsForPrint } = await import("@/lib/print-utils");
    
    const [restaurantInfo, customerOrders, loyaltyPoints, waiterName, ifoodRef, printItems] = await Promise.all([
      fetchRestaurantInfo(order.restaurant_id),
      fetchCustomerOrderCount(order.restaurant_id, order.customer_name, order.customer_phone),
      fetchCustomerLoyaltyPoints(order.restaurant_id, order.customer_id),
      fetchWaiterNameForOrder(order.restaurant_id, order.id),
      fetchIfoodReference(order.id),
      enrichOrderItemsForPrint(order.items),
    ]);

    const receiptData: ReceiptData = {
      restaurant: restaurantInfo,
      orderId: order.id,
      orderType: order.order_type,
      tableNumber: order.table_number,
      customer: { name: order.customer_name, phone: order.customer_phone, totalOrders: Math.max(0, customerOrders - 1), loyaltyPoints },
      waiterName,
      paymentMethod: order.payment_method,
      paymentStatus: order.payment_status,
      items: printItems,
      subtotal: Number(order.subtotal),
      deliveryFee: Number(order.delivery_fee),
      discount: Number(order.discount),
      total: Number(order.total),
      createdAt: order.created_at,
      notes: order.notes,
      scheduledFor: order.scheduled_for,
      deliveryAddress: order.delivery_address,
      ifoodReference: ifoodRef,
    };

    if (order.order_type === "delivery") {
      printer.printDeliveryReceipt(receiptData);
    } else {
      printer.printReceipt(receiptData);
    }
  };

  const getNextStatuses = (current: string) => {
    const flow: Record<string, string[]> = {
      received: ["preparing", "cancelled"],
      preparing: ["ready", "cancelled"],
      ready: ["out_for_delivery", "completed"],
      out_for_delivery: ["completed"],
    };
    return flow[current] || [];
  };

  if (loading) {
    return (
      <DashboardLayout restaurantId={restaurantId}>
        <div className="flex-1 flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const toggleViewMode = (mode: "list" | "cards") => {
    setViewMode(mode);
    localStorage.setItem("pedidos_view_mode", mode);
  };

  return (
    <DashboardLayout restaurantId={restaurantId}>
      <div className="p-4 md:p-6 space-y-5">
        {/* Page header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <ClipboardList className="h-5 w-5 text-primary" />
            <h1 className="font-display font-bold text-lg">Pedidos</h1>
            <Badge variant="secondary" className="text-xs">{filteredOrders.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 mr-2">
              <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
              <span className="text-xs text-muted-foreground hidden sm:inline">Tempo real</span>
            </div>
            {/* View mode toggle */}
            <div className="flex items-center rounded-lg border border-border bg-card overflow-hidden">
              <button
                onClick={() => toggleViewMode("cards")}
                className={`p-2 transition-colors ${viewMode === "cards" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"}`}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => toggleViewMode("list")}
                className={`p-2 transition-colors ${viewMode === "list" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
            <Button variant="ghost" size="icon" onClick={() => restaurantId && fetchOrders(restaurantId)}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {/* Waiter Requests Banner */}
        {waiterRequests.length > 0 && (
          <div className="space-y-2">
            {waiterRequests.map((req: any) => (
              <motion.div
                key={req.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border-2 border-[hsl(35,100%,65%)] bg-[hsl(35,100%,96%)] p-4 flex items-center justify-between shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[hsl(35,100%,88%)] flex items-center justify-center animate-pulse">
                    <Bell className="h-5 w-5 text-[hsl(35,100%,40%)]" />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-[hsl(35,80%,25%)]">🔔 Mesa {req.table_number} solicita garçom!</p>
                    <p className="text-xs text-[hsl(35,50%,45%)]">
                      Solicitado há {Math.max(1, Math.floor((Date.now() - new Date(req.waiter_requested_at).getTime()) / 60000))} min
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => dismissWaiterRequest(req.id)}
                  className="border-[hsl(35,100%,65%)] text-[hsl(35,100%,35%)] hover:bg-[hsl(35,100%,90%)]"
                >
                  Atender
                </Button>
              </motion.div>
            ))}
          </div>
        )}

        {/* Stats cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total", value: stats.total, icon: Package, accent: "text-foreground" },
            { label: "Ativos", value: stats.active, icon: Clock, accent: "text-warning" },
            { label: "Hoje", value: stats.today, icon: CalendarDays, accent: "text-primary" },
            { label: "Faturamento Hoje", value: formatCurrency(stats.todayRevenue), icon: DollarSign, accent: "text-success" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-border bg-gradient-card shadow-card p-4">
              <div className="flex items-center gap-2 mb-1">
                <s.icon className={`h-4 w-4 ${s.accent}`} />
                <span className="text-xs text-muted-foreground">{s.label}</span>
              </div>
              <p className={`text-xl font-bold font-display ${s.accent}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por ID, cliente, telefone, produto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 bg-card border-border"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] h-10 bg-card border-border">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[140px] h-10 bg-card border-border">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              {Object.entries(ORDER_TYPE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.icon} {v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-[130px] h-10 bg-card border-border">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo período</SelectItem>
              <SelectItem value="today">Hoje</SelectItem>
              <SelectItem value="week">Últimos 7 dias</SelectItem>
              <SelectItem value="month">Últimos 30 dias</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
            <SelectTrigger className="w-[140px] h-10 bg-card border-border">
              <SelectValue placeholder="Ordenar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Mais recentes</SelectItem>
              <SelectItem value="oldest">Mais antigos</SelectItem>
              <SelectItem value="highest">Maior valor</SelectItem>
              <SelectItem value="lowest">Menor valor</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Orders */}
        {filteredOrders.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center">
            <ClipboardList className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground font-medium">Nenhum pedido encontrado</p>
            <p className="text-xs text-muted-foreground mt-1">Tente ajustar os filtros</p>
          </div>
        ) : viewMode === "cards" ? (
          /* ==================== CARD VIEW ==================== */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            <AnimatePresence>
              {filteredOrders.map((order) => {
                const st = STATUS_CONFIG[order.status];
                const type = ORDER_TYPE_LABELS[order.order_type];
                const nextStatuses = getNextStatuses(order.status);
                return (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="rounded-xl border border-border bg-card p-4 hover:shadow-md transition-all duration-200 cursor-pointer group"
                    onClick={() => openDetail(order)}
                  >
                    {/* Card header */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="font-display font-bold text-sm text-muted-foreground">
                          #{order.id.slice(0, 6).toUpperCase()}
                        </span>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st?.bg || "bg-muted"}`}>
                        {type?.icon}{" "}
                        {order.table_number
                          ? `Mesa ${order.table_number}`
                          : type?.label || order.order_type}
                      </span>
                    </div>

                    {/* Customer name */}
                    <p className="font-semibold text-sm truncate mb-1">
                      {order.customer_name || "Cliente não informado"}
                    </p>

                    {/* Time */}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                      <Clock className="h-3 w-3" />
                      <span>{timeAgo(order.created_at)}</span>
                    </div>

                    {/* Footer: price + action */}
                    <div className="flex items-center justify-between pt-3 border-t border-border/50">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-bold font-display text-primary">
                          {formatCurrency(Number(order.total))}
                        </span>
                        {order.payment_status === "paid" && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[hsl(142,71%,92%)] text-[hsl(142,71%,30%)] border border-[hsl(142,71%,80%)]">
                            <CheckCircle2 className="h-2.5 w-2.5" />
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {nextStatuses.slice(0, 1).map((ns) => (
                          <Button
                            key={ns}
                            size="sm"
                            variant={ns === "cancelled" ? "destructive" : "default"}
                            className="h-7 text-[10px] px-2 rounded-lg"
                            onClick={() => updateStatus(order.id, ns)}
                          >
                            {STATUS_CONFIG[ns]?.label || ns}
                          </Button>
                        ))}
                        {order.customer_phone && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-[hsl(142,70%,45%)] hover:text-[hsl(142,70%,35%)] hover:bg-[hsl(142,70%,95%)]"
                            onClick={() => openWhatsApp(order.customer_phone, order.customer_name)}
                            title="Falar com cliente no WhatsApp"
                          >
                            <MessageCircle className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => openDetail(order)}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        ) : (
          /* ==================== LIST VIEW ==================== */
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-muted-foreground border-b border-border bg-secondary/30">
                    <th className="text-left p-3 font-medium">Pedido</th>
                    <th className="text-left p-3 font-medium">Cliente</th>
                    <th className="text-left p-3 font-medium hidden sm:table-cell">Tipo</th>
                    <th className="text-left p-3 font-medium hidden md:table-cell">Itens</th>
                    <th className="text-left p-3 font-medium">Total</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium hidden lg:table-cell">Hora</th>
                    <th className="text-right p-3 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {filteredOrders.map((order) => {
                      const st = STATUS_CONFIG[order.status];
                      const type = ORDER_TYPE_LABELS[order.order_type];
                      const nextStatuses = getNextStatuses(order.status);
                      return (
                        <motion.tr
                          key={order.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="border-b border-border/50 hover:bg-secondary/20 transition-colors cursor-pointer"
                          onClick={() => openDetail(order)}
                        >
                          <td className="p-3">
                            <span className="font-display font-bold text-sm">#{order.id.slice(0, 6).toUpperCase()}</span>
                            {order.table_number && (
                              <p className="text-[10px] text-muted-foreground mt-0.5">Mesa {order.table_number}</p>
                            )}
                          </td>
                          <td className="p-3">
                            <p className="text-sm font-medium">{order.customer_name || "—"}</p>
                            {order.customer_phone && (
                              <p className="text-[10px] text-muted-foreground">{order.customer_phone}</p>
                            )}
                          </td>
                          <td className="p-3 hidden sm:table-cell">
                            <span className="text-sm">
                              {type?.icon} {type?.label || order.order_type}
                            </span>
                            {order.scheduled_for && (
                              <p className="text-[10px] text-primary font-medium mt-0.5">
                                📅 {new Date(order.scheduled_for).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} {new Date(order.scheduled_for).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                              </p>
                            )}
                          </td>
                          <td className="p-3 hidden md:table-cell">
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {order.items.length > 0
                                ? order.items.map((i) => `${i.quantity}x ${i.product_name}`).join(", ")
                                : "—"}
                            </p>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-bold font-display">{formatCurrency(Number(order.total))}</span>
                              {order.payment_status === "paid" && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[hsl(142,71%,92%)] text-[hsl(142,71%,30%)] border border-[hsl(142,71%,80%)]">
                                  <CheckCircle2 className="h-2.5 w-2.5" />
                                  Pago
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${st?.bg || "bg-muted"}`}>
                              {st && <st.icon className={`h-3 w-3 ${st.color}`} />}
                              <span className={st?.color}>{st?.label || order.status}</span>
                            </span>
                          </td>
                          <td className="p-3 hidden lg:table-cell">
                            <span className="text-xs text-muted-foreground">{timeAgo(order.created_at)}</span>
                          </td>
                          <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              {nextStatuses.slice(0, 1).map((ns) => (
                                <Button
                                  key={ns}
                                  size="sm"
                                  variant={ns === "cancelled" ? "destructive" : "default"}
                                  className="h-7 text-[11px] px-2.5"
                                  onClick={() => updateStatus(order.id, ns)}
                                >
                                  {STATUS_CONFIG[ns]?.label || ns}
                                </Button>
                              ))}
                              {order.customer_phone && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-[hsl(142,70%,45%)] hover:text-[hsl(142,70%,35%)] hover:bg-[hsl(142,70%,95%)]"
                                  onClick={() => openWhatsApp(order.customer_phone, order.customer_name)}
                                  title="Falar com cliente no WhatsApp"
                                >
                                  <MessageCircle className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => openDetail(order)}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Order Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedOrder && (() => {
            const order = selectedOrder;
            const st = STATUS_CONFIG[order.status];
            const type = ORDER_TYPE_LABELS[order.order_type];
            const nextStatuses = getNextStatuses(order.status);

            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display flex items-center gap-2">
                    Pedido #{order.id.slice(0, 6).toUpperCase()}
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${st?.bg}`}>
                      {st && <st.icon className={`h-3 w-3 ${st.color}`} />}
                      <span className={st?.color}>{st?.label}</span>
                    </span>
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Info grid */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-[10px] text-muted-foreground">Data/Hora</p>
                        <p className="font-medium">{formatDateTime(order.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-[10px] text-muted-foreground">Tipo</p>
                        <p className="font-medium">{type?.icon} {type?.label}</p>
                      </div>
                    </div>
                    {order.customer_name && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-[10px] text-muted-foreground">Cliente</p>
                          <p className="font-medium">{order.customer_name}</p>
                        </div>
                      </div>
                    )}
                    {order.customer_phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-[10px] text-muted-foreground">Telefone</p>
                          <p className="font-medium">{order.customer_phone}</p>
                        </div>
                      </div>
                    )}
                    {order.table_number && (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-lg">🪑</span>
                        <div>
                          <p className="text-[10px] text-muted-foreground">Mesa</p>
                          <p className="font-medium">{order.table_number}</p>
                        </div>
                      </div>
                    )}
                    {order.payment_method && (
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-[10px] text-muted-foreground">Pagamento</p>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{PAYMENT_LABELS[order.payment_method] || order.payment_method}</p>
                            {order.payment_status === "paid" && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[hsl(142,71%,92%)] text-[hsl(142,71%,30%)] border border-[hsl(142,71%,80%)]">
                                <CheckCircle2 className="h-3 w-3" />
                                Pago
                              </span>
                            )}
                            {order.payment_status === "pending" && order.payment_method === "mercadopago" && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[hsl(45,100%,92%)] text-[hsl(45,100%,30%)] border border-[hsl(45,100%,75%)]">
                                Pendente
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Delivery address */}
                  {order.delivery_address && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary/50 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-[10px] text-muted-foreground">Endereço de Entrega</p>
                        <p>{order.delivery_address}</p>
                      </div>
                    </div>
                  )}

                  {/* Items */}
                  <div>
                    <h3 className="text-sm font-semibold mb-2">Itens do Pedido</h3>
                    <div className="rounded-lg border border-border overflow-hidden">
                      {order.items.length > 0 ? order.items.map((item, idx) => (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between px-3 py-2.5 text-sm ${idx < order.items.length - 1 ? "border-b border-border/50" : ""}`}
                        >
                          <div className="flex-1">
                            <span className="font-semibold text-primary">{item.quantity}x</span>{" "}
                            <span className="font-medium">{item.product_name}</span>
                            {item.notes && (
                              <p className="text-[10px] text-warning mt-0.5">📝 {item.notes}</p>
                            )}
                          </div>
                          <span className="font-medium text-sm">{formatCurrency(Number(item.total_price))}</span>
                        </div>
                      )) : (
                        <p className="p-3 text-xs text-muted-foreground italic">Sem itens detalhados</p>
                      )}
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="rounded-lg bg-secondary/30 p-3 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>{formatCurrency(Number(order.subtotal))}</span>
                    </div>
                    {Number(order.delivery_fee) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Taxa de entrega</span>
                        <span>{formatCurrency(Number(order.delivery_fee))}</span>
                      </div>
                    )}
                    {Number(order.discount) > 0 && (
                      <div className="flex justify-between text-success">
                        <span>Desconto</span>
                        <span>-{formatCurrency(Number(order.discount))}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-1 border-t border-border font-bold text-base">
                      <span>Total</span>
                      <span>{formatCurrency(Number(order.total))}</span>
                    </div>
                  </div>

                  {/* Notes */}
                  {order.notes && (
                    <div className="text-sm bg-warning/10 text-warning border border-warning/20 rounded-lg px-3 py-2">
                      📝 {order.notes}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    {nextStatuses.map((ns) => (
                      <Button
                        key={ns}
                        size="sm"
                        variant={ns === "cancelled" ? "destructive" : "default"}
                        className="gap-1.5"
                        onClick={() => updateStatus(order.id, ns)}
                      >
                        {(() => { const Icon = STATUS_CONFIG[ns]?.icon; return Icon ? <Icon className="h-3.5 w-3.5" /> : null; })()}
                        {STATUS_CONFIG[ns]?.label || ns}
                      </Button>
                    ))}
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => {
                      const baseUrl = window.location.origin;
                      const trackingUrl = `${baseUrl}/acompanhar?pedido=${order.id}${restaurantSlug ? `&r=${restaurantSlug}` : ''}`;
                      navigator.clipboard.writeText(trackingUrl);
                      toast({ title: "Link copiado!", description: "Link de acompanhamento copiado para a área de transferência." });
                    }}>
                      <Link2 className="h-3.5 w-3.5" />
                      Copiar link
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => printOrder(order)}>
                      <Printer className="h-3.5 w-3.5" />
                      Imprimir
                    </Button>
                    {order.customer_phone && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 text-[hsl(142,70%,45%)] border-[hsl(142,70%,80%)] hover:bg-[hsl(142,70%,95%)] hover:text-[hsl(142,70%,35%)]"
                        onClick={() => openWhatsApp(order.customer_phone, order.customer_name)}
                      >
                        <MessageCircle className="h-3.5 w-3.5" />
                        Falar com Cliente
                      </Button>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Pedidos;
